'use client'

import { useEffect, useMemo, useRef } from 'react'
import { useNotebookRouteDomSync } from '@/lib/notebook/core/use-notebook-route-dom-sync'
import { useRouter } from 'next/navigation'
import { useNotebook } from '@/lib/notebook/core/store'
import { nbWarn } from '@/lib/notebook/core/notebook-debug'
import TopBar from '@/lib/notebook/chrome/toolbar/TopBar'
import NotebookAtmosphere from '@/lib/notebook/editor/shell/NotebookAtmosphere'
import AddSectionFab from '@/lib/notebook/chrome/floating/AddSectionFab'
import EmptyState from './EmptyState'
import SectionLauncher from '@/lib/notebook/editor/launcher/SectionLauncher'
import BlockSection from '@/lib/notebook/editor/sections/BlockSection'
import TabsWrapper from '@/lib/notebook/Blocks/tabs/TabsWrapper'
import { WorkspaceLimitDialog } from '@/components/freemium/WorkspaceLimitDialog'
import { NotebookContentColumn } from '@/lib/notebook/layout/NotebookContentColumn'
import { ArchiveStatusBanner } from '@/lib/notebook/editor/shell/ArchiveStatusBanner'
import { DeletedStatusBanner } from '@/lib/notebook/editor/shell/DeletedStatusBanner'
import { PlanReadOnlyDialog } from '@/components/freemium/PlanReadOnlyDialog'
import { useNotebookLifecycleSync } from '@/lib/notebook/lifecycle/use-notebook-lifecycle-sync'
import { PublicationIdentity } from '@/lib/notebook/editor/shell/PublicNotebookChrome'
import {
  notifyTabBlockSessionEntry,
  notifyTabBlockThresholdCrossed,
} from '@/lib/notebook/layout/tab-block-limit-toasts'
import {
  dedupeContentSections,
  findHeroSection,
  findTabsSection,
  getActiveTabContentSections,
} from '@/lib/notebook/layout/notebook-layout'

export default function NotebookShell({ notebookId, variant = 'editor' }) {
  const isPublic = variant === 'public'
  const hydrated = useNotebook((s) => s.hydrated)
  const sessionEpoch = useNotebook((s) => s.sessionEpoch)
  const contentReadyEpoch = useNotebook((s) => s.contentReadyEpoch)
  const sessionNotebookId = useNotebook((s) => s.notebookId)
  const notebook = useNotebook((s) => s.notebook)
  const activeTabId = useNotebook((s) => s.activeTabId)
  const routeVisit = useNotebook((s) => s.routeVisit)
  const locked = useNotebook((s) => s.locked)
  const isArchived = useNotebook((s) => s.isArchived)
  const lifecycleView = useNotebook((s) => s.lifecycleView)
  const launcherOpen = useNotebook((s) => s.launcherOpen)
  const openLauncher = useNotebook((s) => s.openLauncher)
  const setLauncherOpen = useNotebook((s) => s.setLauncherOpen)
  const freemiumPrompt = useNotebook((s) => s.freemiumPrompt)
  const planAccessPrompt = useNotebook((s) => s.planAccessPrompt)
  const dismissFreemiumPrompt = useNotebook((s) => s.dismissFreemiumPrompt)
  const dismissPlanAccessPrompt = useNotebook((s) => s.dismissPlanAccessPrompt)
  const router = useRouter()

  const heroSection = useMemo(
    () => findHeroSection(notebook)?.section ?? null,
    [notebook],
  )

  const tabsSection = useMemo(
    () => findTabsSection(notebook, activeTabId)?.section ?? null,
    [notebook, activeTabId],
  )

  const contentSections = useMemo(() => {
    const sections = getActiveTabContentSections(notebook, activeTabId)
    return dedupeContentSections(sections)
  }, [notebook, activeTabId])

  const sessionReady =
    hydrated &&
    sessionNotebookId === notebookId &&
    contentReadyEpoch === sessionEpoch &&
    (isPublic || routeVisit >= 0)

  const showBlocks = sessionReady
  const showDeletedBanner = lifecycleView === 'deleted'
  const showArchivedBanner = !showDeletedBanner && isArchived

  useNotebookLifecycleSync(notebookId, sessionReady && !isPublic)

  const prevBlockCountRef = useRef(0)
  const tabLimitsInitializedRef = useRef(false)

  useEffect(() => {
    if (!isPublic || !sessionReady || !activeTabId) return
    const count = contentSections.length

    if (!tabLimitsInitializedRef.current) {
      tabLimitsInitializedRef.current = true
      notifyTabBlockSessionEntry(activeTabId, count)
      prevBlockCountRef.current = count
      return
    }

    const prev = prevBlockCountRef.current
    notifyTabBlockThresholdCrossed(activeTabId, count, prev)
    prevBlockCountRef.current = count
  }, [isPublic, sessionReady, activeTabId, contentSections.length])

  useEffect(() => {
    prevBlockCountRef.current = 0
    tabLimitsInitializedRef.current = false
  }, [activeTabId, sessionEpoch])

  useEffect(() => {
    if (!sessionReady) return
    const raw = getActiveTabContentSections(notebook, activeTabId)
    const unique = dedupeContentSections(raw)
    if (raw.length !== unique.length) {
      nbWarn('Duplicate section ids removed before render', {
        rawCount: raw.length,
        uniqueCount: unique.length,
        ids: raw.map((s) => s.id),
      })
    }
  }, [sessionReady, notebook, activeTabId])

  useNotebookRouteDomSync(sessionReady, [
    notebookId,
    sessionEpoch,
    contentSections.length,
  ])

  useEffect(() => {
    if (!sessionReady || typeof window === 'undefined') return undefined
    window.scrollTo(0, 0)
    return undefined
  }, [sessionReady, notebookId, sessionEpoch, activeTabId])

  useEffect(() => {
    if (isPublic) return undefined
    const onKey = (e) => {
      if (locked) return
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault()
        openLauncher()
      } else if (e.key === 'Escape') setLauncherOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isPublic, locked, openLauncher, setLauncherOpen])

  return (
    <div
      className={`relative min-h-screen notebook-surface nb-editor-canvas overflow-x-hidden${isPublic ? ' nb-public-shell' : ''}`}
    >
      <NotebookAtmosphere />
      {isPublic ? (
        <PublicationIdentity updatedAt={notebook?.updated_at} />
      ) : null}
      {!isPublic ? <TopBar /> : null}

      <main className={`relative z-10 pb-40${isPublic ? ' pt-2' : ' pt-[4.5rem]'}`}>
        {showBlocks && showDeletedBanner ? (
          <div className="nb-deleted-status-wrap">
            <DeletedStatusBanner />
          </div>
        ) : null}
        <NotebookContentColumn>
          {showBlocks && showArchivedBanner ? <ArchiveStatusBanner /> : null}
          {!showBlocks ? (
            <div className="nb-content-loading" aria-busy="true">
              <div className="nb-loading-pulse" />
            </div>
          ) : (
            <>
              {heroSection && (
                <BlockSection
                  key={`${heroSection.id}-${sessionEpoch}`}
                  section={heroSection}
                  index={0}
                  bodyPriority={isPublic}
                />
              )}
              {tabsSection ? (
                <BlockSection
                  key={`${tabsSection.id}-${sessionEpoch}`}
                  section={tabsSection}
                  index={1}
                />
              ) : (
                <article
                  className="nb-block-slot nb-block-slot--structural"
                  data-block-type="block_tabs"
                >
                  <div className="nb-block-slot__body">
                    <div className="bible nb-block-host nb-block-host--tabs">
                      <TabsWrapper
                        sectionId=""
                        data={{}}
                        locked={locked}
                        structural
                      />
                    </div>
                  </div>
                </article>
              )}

              {contentSections.length === 0 && !isPublic ? (
                <EmptyState />
              ) : (
                contentSections.map((section, i) => (
                  <BlockSection
                    key={`${section.id}-${sessionEpoch}`}
                    section={section}
                    deferBody={i > 0}
                    bodyPriority={i === 0}
                  />
                ))
              )}
            </>
          )}
        </NotebookContentColumn>
      </main>

      {!isPublic ? <AddSectionFab /> : null}

      {!isPublic && launcherOpen ? (
        <SectionLauncher onClose={() => setLauncherOpen(false)} />
      ) : null}

      {!isPublic ? (
        <>
          <WorkspaceLimitDialog
            open={!!freemiumPrompt}
            kind={freemiumPrompt}
            onOpenChange={(open) => {
              if (!open) dismissFreemiumPrompt()
            }}
            onNavigateHome={
              freemiumPrompt === 'notebook'
                ? () => {
                    dismissFreemiumPrompt()
                    router.push('/home')
                  }
                : undefined
            }
          />
          <PlanReadOnlyDialog
            open={!!planAccessPrompt}
            kind={planAccessPrompt}
            onOpenChange={(open) => {
              if (!open) dismissPlanAccessPrompt()
            }}
          />
        </>
      ) : null}
    </div>
  )
}
